import { OpenTimeSeries, ValueType } from "@captor/openseries-ts";

/** Matches the Python helper used to pick bar resampling frequency. */
export type BarFreq = "BME" | "BQE" | "BYE";
export type BarResampleFreq = "ME" | "QE" | "YE";

type DateLike = Date | string;

type YearMonthDay = {
    year: number;
    month: number;
    day: number;
};

function toYearMonthDay(input: DateLike): YearMonthDay | null {
    if (typeof input === "string") {
        const match = input.match(/^(\d{4})-(\d{2})(?:-(\d{2}))?/);
        if (!match) return null;
        const year = Number(match[1]);
        const month = Number(match[2]);
        const day = match[3] ? Number(match[3]) : 1;
        if (!year || month < 1 || month > 12 || day < 1 || day > 31) return null;
        return { year, month, day };
    }
    if (input instanceof Date && !Number.isNaN(input.getTime())) {
        return { year: input.getUTCFullYear(), month: input.getUTCMonth() + 1, day: input.getUTCDate() };
    }
    return null;
}

function compareYearMonthDay(a: YearMonthDay, b: YearMonthDay): number {
    if (a.year !== b.year) return a.year - b.year;
    if (a.month !== b.month) return a.month - b.month;
    return a.day - b.day;
}

/**
 * Business month / quarter / year-end frequency from a date span.
 * Same rules as Python `bar_freq_for_period`:
 * - under 13 months → BME
 * - 13–36 months → BQE
 * - over 36 months → BYE
 */
export function barFreqForPeriod(startIdx: DateLike, endIdx: DateLike): BarFreq {
    let start = toYearMonthDay(startIdx);
    let end = toYearMonthDay(endIdx);
    if (!start || !end) return "BYE";
    if (compareYearMonthDay(end, start) < 0) {
        const swapped = start;
        start = end;
        end = swapped;
    }
    const monthsSpan = (end.year - start.year) * 12 + (end.month - start.month);
    if (monthsSpan < 13) return "BME";
    if (monthsSpan <= 36) return "BQE";
    return "BYE";
}

export function barFreqToResampleFreq(freq: BarFreq): BarResampleFreq {
    if (freq === "BME") return "ME";
    if (freq === "BQE") return "QE";
    return "YE";
}

export function formatBarPeriodLabel(dateStr: string, freq: BarFreq): string {
    const year = Number(dateStr.slice(0, 4));
    const month = Number(dateStr.slice(5, 7));
    if (!year || !month) return dateStr;
    if (freq === "BYE") return String(year);
    if (freq === "BQE") return `Q${Math.ceil(month / 3)} ${year}`;
    const date = new Date(Date.UTC(year, month - 1, 1));
    return date.toLocaleString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
}

/**
 * Period-over-period returns at business period-end. Includes a partial first
 * period when the series starts after the previous period-end.
 */
export function periodBarReturns(ts: OpenTimeSeries, resampleFreq: BarResampleFreq): { dates: string[]; returns: number[] } {
    if (!ts || ts.length === 0) return { dates: [], returns: [] };

    const clone = ts.fromDeepcopy();
    if (clone.valuetype === ValueType.RTRN) {
        clone.toCumret();
    }

    const firstDate = ts.firstIdx || clone.firstIdx;
    const firstValue = ts.getTsdfValues()[0];
    clone.resampleToPeriodEnd(resampleFreq);

    const dates = clone.getTsdfDates();
    const values = clone.getTsdfValues();
    const levelDates: string[] = [];
    const levelValues: number[] = [];

    if (firstDate && typeof firstValue === "number" && (dates.length === 0 || dates[0] > firstDate)) {
        levelDates.push(firstDate);
        levelValues.push(firstValue);
    }
    for (let i = 0; i < dates.length; i++) {
        if (typeof values[i] !== "number") continue;
        levelDates.push(dates[i]);
        levelValues.push(values[i]);
    }

    const outDates: string[] = [];
    const outReturns: number[] = [];
    for (let i = 1; i < levelValues.length; i++) {
        const prev = levelValues[i - 1];
        const curr = levelValues[i];
        if (prev === 0) continue;
        outDates.push(levelDates[i]);
        outReturns.push(curr / prev - 1);
    }
    return { dates: outDates, returns: outReturns };
}
